/**
 * `realtimeAudio.ts` — browser audio halves of Unity's
 * `AIAgentTransportWebSocketAudio.cs`.
 *
 * Mic capture: getUserMedia → AudioWorklet → downsample to 24 kHz mono
 * PCM16 → base64 chunks (~50 ms cadence, mirroring Unity's mic pump).
 * Playback: base64 PCM16 24 kHz deltas scheduled back-to-back on an
 * AudioContext timeline (Unity's ring-buffer AudioSource equivalent).
 * On mute we append ~800 ms of silence so server VAD closes the user turn
 * (Unity parity: `ServerVADSilenceDurationMs + 300`).
 */

import { Emitter } from "@core/events/emitter";
import {
  K_MIC_HEALTH_POLL_MS,
  K_MIC_TRACK_MUTE_GRACE_MS,
  MicrophoneHealthMonitor,
  MicrophoneRestartBackoff,
  microphoneRecoveredNote,
  microphoneRestartedNote,
  type MicrophoneDiagnostic,
  type MicrophoneRestartReason,
} from "./microphoneHealth";

export const REALTIME_SAMPLE_RATE = 24000;
/** Mic pump cadence (samples per flush at 24 kHz ≈ 50 ms). */
const MIC_FLUSH_SAMPLES = 1200;
const MUTE_SILENCE_MS = 800;

/**
 * Microphone constraints, shared by BOTH transports so they cannot drift.
 *
 * `echoCancellation` is the one that bites: both transports render the agent's
 * audio to the speakers, so a mic captured without AEC feeds the agent back
 * into itself and it interrupts its own answer.
 */
export const K_REALTIME_MIC_CONSTRAINTS: MediaTrackConstraints = {
  channelCount: 1,
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

// Inline AudioWorklet that forwards raw Float32 mono frames to the main thread.
const CAPTURE_WORKLET_SOURCE = `
class CampfireCaptureProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const channel = inputs[0] && inputs[0][0];
    if (channel && channel.length > 0) {
      this.port.postMessage(channel.slice(0));
    }
    return true;
  }
}
registerProcessor("campfire-capture", CampfireCaptureProcessor);
`;

export class RealtimeMicCapture {
  /** `microphone.restarted` / `microphone.recovered` — see `microphoneHealth.ts`. */
  readonly onDiagnostic = new Emitter<MicrophoneDiagnostic>();

  private m_context: AudioContext | null = null;
  private m_stream: MediaStream | null = null;
  private m_source: MediaStreamAudioSourceNode | null = null;
  private m_worklet: AudioWorkletNode | null = null;
  private m_pending: number[] = [];
  private m_muted = true;
  private m_onChunk: ((base64Pcm16: string) => void) | null = null;
  private m_speechLevel = 0;
  /** Cumulative capture counters fed to `m_health`; they run while muted too. */
  private m_capturedSeconds = 0;
  private m_nonSilentFrames = 0;
  private readonly m_health = new MicrophoneHealthMonitor();
  private readonly m_restart = new MicrophoneRestartBackoff();
  private m_healthTimer: number | null = null;
  private m_muteTimer: number | null = null;
  private m_reacquiring = false;
  private m_unwatchTrack: (() => void) | null = null;

  get isCapturing(): boolean {
    return this.m_stream !== null;
  }

  /** Smoothed 0..1 input level for UI meters. */
  get inputLevel(): number {
    return this.m_speechLevel;
  }

  async startAsync(onChunk: (base64Pcm16: string) => void): Promise<boolean> {
    if (this.m_stream) {
      this.m_onChunk = onChunk;
      return true;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: K_REALTIME_MIC_CONSTRAINTS,
      });
      const context = new AudioContext({ sampleRate: REALTIME_SAMPLE_RATE });
      const workletUrl = URL.createObjectURL(
        new Blob([CAPTURE_WORKLET_SOURCE], { type: "application/javascript" }),
      );
      try {
        await context.audioWorklet.addModule(workletUrl);
      } finally {
        URL.revokeObjectURL(workletUrl);
      }

      const source = context.createMediaStreamSource(stream);
      const worklet = new AudioWorkletNode(context, "campfire-capture", {
        numberOfInputs: 1,
        numberOfOutputs: 0,
      });
      worklet.port.onmessage = (e: MessageEvent<Float32Array>) => {
        this.handleFrames(e.data, context.sampleRate);
      };
      source.connect(worklet);

      this.m_context = context;
      this.m_stream = stream;
      this.m_source = source;
      this.m_worklet = worklet;
      this.m_onChunk = onChunk;
      this.watchTrack(stream);
      if (!this.m_muted) this.startHealthPoll();
      return true;
    } catch (err) {
      console.warn("[RealtimeMicCapture] microphone unavailable:", err);
      this.stop();
      return false;
    }
  }

  setMuted(muted: boolean): void {
    if (this.m_muted === muted) return;
    this.m_muted = muted;
    if (muted) {
      this.stopHealthPoll();
      this.flushPending();
      this.appendSilence(MUTE_SILENCE_MS);
      this.m_speechLevel = 0;
    } else {
      void this.m_context?.resume();
      if (this.m_stream) this.startHealthPoll();
    }
  }

  get isMuted(): boolean {
    return this.m_muted;
  }

  stop(): void {
    this.stopHealthPoll();
    this.m_unwatchTrack?.();
    this.m_unwatchTrack = null;
    this.m_restart.reset();
    this.m_worklet?.port.close();
    this.m_worklet?.disconnect();
    this.m_source?.disconnect();
    this.m_stream?.getTracks().forEach((t) => t.stop());
    void this.m_context?.close().catch(() => undefined);
    this.m_worklet = null;
    this.m_source = null;
    this.m_stream = null;
    this.m_context = null;
    this.m_pending = [];
    this.m_onChunk = null;
    this.m_speechLevel = 0;
  }

  private handleFrames(frames: Float32Array, contextRate: number): void {
    let level = 0;
    for (const sample of frames) level = Math.max(level, Math.abs(sample));
    this.m_capturedSeconds += frames.length / contextRate;
    if (level > 0) this.m_nonSilentFrames += 1;
    if (this.m_muted || !this.m_onChunk) return;

    this.m_speechLevel = this.m_speechLevel * 0.85 + level * 0.15;

    const resampled =
      contextRate === REALTIME_SAMPLE_RATE
        ? frames
        : linearResample(frames, contextRate, REALTIME_SAMPLE_RATE);
    for (const sample of resampled) this.m_pending.push(sample);
    if (this.m_pending.length >= MIC_FLUSH_SAMPLES) this.flushPending();
  }

  private flushPending(): void {
    if (this.m_pending.length === 0 || !this.m_onChunk) return;
    const chunk = this.m_pending;
    this.m_pending = [];
    this.m_onChunk(floatToPcm16Base64(chunk));
  }

  private appendSilence(ms: number): void {
    if (!this.m_onChunk) return;
    const samples = Math.round((ms / 1000) * REALTIME_SAMPLE_RATE);
    this.m_onChunk(floatToPcm16Base64(new Array<number>(samples).fill(0)));
  }

  private watchTrack(stream: MediaStream): void {
    this.m_unwatchTrack?.();
    const track = stream.getAudioTracks()[0];
    if (!track) return;
    const isCurrent = () => stream === this.m_stream;
    const onEnded = () => {
      if (isCurrent()) void this.reacquireAsync("track_ended");
    };
    const onMute = () => {
      this.clearMuteTimer();
      this.m_muteTimer = window.setTimeout(() => {
        this.m_muteTimer = null;
        if (isCurrent() && track.muted) void this.reacquireAsync("track_muted");
      }, K_MIC_TRACK_MUTE_GRACE_MS);
    };
    const onUnmute = () => this.clearMuteTimer();
    const onDeviceChange = () => {
      if (isCurrent() && (track.readyState === "ended" || track.muted)) {
        void this.reacquireAsync("device_changed");
      }
    };
    track.addEventListener("ended", onEnded);
    track.addEventListener("mute", onMute);
    track.addEventListener("unmute", onUnmute);
    navigator.mediaDevices.addEventListener("devicechange", onDeviceChange);
    this.m_unwatchTrack = () => {
      track.removeEventListener("ended", onEnded);
      track.removeEventListener("mute", onMute);
      track.removeEventListener("unmute", onUnmute);
      navigator.mediaDevices.removeEventListener("devicechange", onDeviceChange);
      this.clearMuteTimer();
    };
  }

  private clearMuteTimer(): void {
    if (this.m_muteTimer === null) return;
    window.clearTimeout(this.m_muteTimer);
    this.m_muteTimer = null;
  }

  private startHealthPoll(): void {
    this.stopHealthPoll();
    this.m_health.reset(performance.now());
    this.m_healthTimer = window.setInterval(() => this.checkHealth(), K_MIC_HEALTH_POLL_MS);
  }

  private stopHealthPoll(): void {
    if (this.m_healthTimer === null) return;
    window.clearInterval(this.m_healthTimer);
    this.m_healthTimer = null;
  }

  private checkHealth(): void {
    if (!this.m_stream || this.m_reacquiring) return;
    const now = performance.now();
    const stall = this.m_health.observe(this.m_capturedSeconds, this.m_nonSilentFrames, now);
    if (stall !== null) {
      void this.reacquireAsync(stall);
      return;
    }
    if (!this.m_health.isDeliveringAudio) return;
    const recovered = this.m_restart.recovered(now);
    if (recovered) {
      this.onDiagnostic.emit(
        microphoneRecoveredNote(recovered.attempts, recovered.lostMs, "websocket"),
      );
    }
  }

  /** Swap the source node onto a fresh capture; the context and worklet stay. */
  private async reacquireAsync(reason: MicrophoneRestartReason): Promise<void> {
    const context = this.m_context;
    const worklet = this.m_worklet;
    if (this.m_reacquiring || !context || !worklet || !this.m_stream) return;
    const now = performance.now();
    const lostSince = this.m_health.lastAudioAt;
    if (!this.m_restart.tryBegin(now, lostSince)) return;
    const attempt = this.m_restart.attempts;
    const stalledMs = lostSince > 0 ? now - lostSince : 0;
    this.m_reacquiring = true;
    let started = false;
    // Release the dead capture first: some platforms refuse a second
    // concurrent capture while the old one is still open.
    this.m_unwatchTrack?.();
    this.m_unwatchTrack = null;
    this.m_source?.disconnect();
    this.m_source = null;
    this.m_stream.getTracks().forEach((t) => t.stop());
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: K_REALTIME_MIC_CONSTRAINTS,
      });
      if (context !== this.m_context || worklet !== this.m_worklet) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      // A recorder can also suspend or interrupt the context, which starves
      // the worklet just like a dead track.
      await context.resume();
      const source = context.createMediaStreamSource(stream);
      source.connect(worklet);
      this.m_stream = stream;
      this.m_source = source;
      this.watchTrack(stream);
      this.m_health.reset(performance.now());
      started = true;
    } catch (err) {
      console.warn("[RealtimeMicCapture] microphone reacquire failed:", err);
    } finally {
      this.m_reacquiring = false;
      this.onDiagnostic.emit(
        microphoneRestartedNote(reason, attempt, stalledMs, started, "websocket"),
      );
    }
  }
}

export class RealtimePcmPlayback {
  /** Fires on speaking-state transitions (Unity `AgentSpeakingStateChanged`
   *  source): true on first queued audio, false when the queue drains. */
  readonly onSpeakingChanged = new Emitter<boolean>();

  private m_context: AudioContext | null = null;
  private m_nextStartTime = 0;
  private m_activeSources = new Set<AudioBufferSourceNode>();
  private m_speakingState = false;
  private m_drainTimer: number | null = null;

  get isSpeaking(): boolean {
    const context = this.m_context;
    return !!context && this.m_nextStartTime > context.currentTime + 0.02;
  }

  enqueueBase64Pcm16(base64: string): void {
    const context = this.ensureContext();
    const float = pcm16Base64ToFloat(base64);
    if (float.length === 0) return;

    const buffer = context.createBuffer(1, float.length, REALTIME_SAMPLE_RATE);
    buffer.getChannelData(0).set(float);
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(context.destination);

    const startAt = Math.max(context.currentTime + 0.02, this.m_nextStartTime);
    source.start(startAt);
    this.m_nextStartTime = startAt + buffer.duration;
    this.m_activeSources.add(source);
    source.onended = () => this.m_activeSources.delete(source);
    this.setSpeakingState(true);
    this.scheduleDrainCheck();
  }

  /** Drop everything queued (Unity `StopOutputAudioPlayback`). */
  stopPlayback(): void {
    for (const source of this.m_activeSources) {
      try {
        source.stop();
      } catch {
        // already stopped
      }
    }
    this.m_activeSources.clear();
    this.m_nextStartTime = 0;
    this.clearDrainTimer();
    this.setSpeakingState(false);
  }

  private scheduleDrainCheck(): void {
    this.clearDrainTimer();
    const context = this.m_context;
    if (!context) return;
    const remainingMs = Math.max(0, (this.m_nextStartTime - context.currentTime) * 1000);
    this.m_drainTimer = window.setTimeout(() => {
      this.m_drainTimer = null;
      if (this.isSpeaking) {
        this.scheduleDrainCheck();
      } else {
        this.setSpeakingState(false);
      }
    }, remainingMs + 80);
  }

  private clearDrainTimer(): void {
    if (this.m_drainTimer !== null) {
      window.clearTimeout(this.m_drainTimer);
      this.m_drainTimer = null;
    }
  }

  private setSpeakingState(speaking: boolean): void {
    if (this.m_speakingState === speaking) return;
    this.m_speakingState = speaking;
    this.onSpeakingChanged.emit(speaking);
  }

  resumeIfNeeded(): void {
    void this.m_context?.resume();
  }

  dispose(): void {
    this.stopPlayback();
    this.clearDrainTimer();
    void this.m_context?.close().catch(() => undefined);
    this.m_context = null;
  }

  private ensureContext(): AudioContext {
    if (!this.m_context) {
      this.m_context = new AudioContext({ sampleRate: REALTIME_SAMPLE_RATE });
    }
    void this.m_context.resume();
    return this.m_context;
  }
}

// ---------------------------------------------------------------------------
// PCM16 <-> base64 helpers
// ---------------------------------------------------------------------------

function floatToPcm16Base64(samples: ArrayLike<number>): string {
  const pcm = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    const clamped = Math.max(-1, Math.min(1, samples[i]));
    pcm[i] = Math.round(clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff);
  }
  const bytes = new Uint8Array(pcm.buffer);
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

function pcm16Base64ToFloat(base64: string): Float32Array {
  let binary: string;
  try {
    binary = atob(base64);
  } catch {
    return new Float32Array(0);
  }
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const pcm = new Int16Array(bytes.buffer, 0, Math.floor(bytes.length / 2));
  const float = new Float32Array(pcm.length);
  for (let i = 0; i < pcm.length; i++) {
    float[i] = pcm[i] / (pcm[i] < 0 ? 0x8000 : 0x7fff);
  }
  return float;
}

function linearResample(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate || input.length === 0) return input;
  const ratio = fromRate / toRate;
  const outLength = Math.max(1, Math.floor(input.length / ratio));
  const output = new Float32Array(outLength);
  for (let i = 0; i < outLength; i++) {
    const pos = i * ratio;
    const i0 = Math.floor(pos);
    const i1 = Math.min(input.length - 1, i0 + 1);
    const frac = pos - i0;
    output[i] = input[i0] * (1 - frac) + input[i1] * frac;
  }
  return output;
}
